import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DeepLinkBanner from '../components/DeepLinkBanner/DeepLinkBanner';
import {
  getPlatform,
  getStoreUrl,
  tryOpenApp,
  getPageTitle,
  getPageDescription,
  getDeepLinkUrl,
} from '../utils/deeplink';
import { getEntityFromRTDB } from '../utils/firebaseAppDb';

const VALID_TYPES = ['user', 'group', 'room', 'chat'];

export default function DeepLinkPage() {
  const params = useParams();

  let type, identifier;
  if (params.username) { type = 'user'; identifier = params.username; }
  else if (params.groupId) { type = 'group'; identifier = params.groupId; }
  else if (params.roomId) { type = 'room'; identifier = params.roomId; }
  else if (params.chatId) { type = 'chat'; identifier = params.chatId; }
  else { type = null; identifier = ''; }

  const [platform, setPlatform] = useState('desktop');
  const [appOpened, setAppOpened] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [entityData, setEntityData] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setPlatform(getPlatform());
    if (!type) return;
    let cancelled = false;
    getEntityFromRTDB(type, identifier).then((data) => {
      if (!cancelled) setEntityData(data);
    });
    return () => { cancelled = true; };
  }, [type, identifier]);

  const handleOpenApp = () => {
    if (platform === 'desktop') return;

    timerRef.current = setTimeout(() => {
      setTimedOut(true);
      const storeUrl = getStoreUrl(platform);
      if (storeUrl && storeUrl !== '#') {
        window.location.href = storeUrl;
      }
    }, 2000);

    const handleVisibility = () => {
      if (document.hidden) {
        setAppOpened(true);
        if (timerRef.current) clearTimeout(timerRef.current);
      }
    };

    const handleBlur = () => {
      setAppOpened(true);
      if (timerRef.current) clearTimeout(timerRef.current);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);

    tryOpenApp(platform, type, identifier);

    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
    }, 3000);
  };

  if (!type) {
    return (
      <div className="deeplink-wrapper">
        <div className="deeplink-card">
          <h1 className="deeplink-title">Invalid Link</h1>
          <p className="deeplink-subtitle">
            This link doesn't match any known SoloChat Messenger content.
          </p>
          <a href="https://solochat.me" className="deeplink-open-btn">
            Go to SoloChat
          </a>
        </div>
      </div>
    );
  }

  const entityName = entityData?.name || null;
  const entityImage = entityData?.imageUrl || entityData?.image || entityData?.imageurl || entityData?.photoUrl || entityData?.photoURL || entityData?.avatar || entityData?.profileImage || null;
  const title = entityName ? `${entityName} - SoloChat` : getPageTitle(type, identifier);
  const description = entityName
    ? `View ${entityName} on SoloChat`
    : getPageDescription(type, identifier);
  const canonicalUrl = getDeepLinkUrl(type, identifier);
  const socialImage = entityImage;
  const siteName = 'SoloChat';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:locale" content="en_US" />
        {socialImage && <meta property="og:image" content={socialImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {socialImage && <meta name="twitter:image" content={socialImage} />}
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <DeepLinkBanner
        type={type}
        identifier={identifier}
        platform={platform}
        appOpened={appOpened}
        timedOut={timedOut}
        entityData={entityData}
        onOpenApp={handleOpenApp}
      />
    </>
  );
}
